// Opatření (seznam, detail, změna stavu).
// (Krok B1b: rozděleno ze src/app.js beze změny kódu; importy/exporty doplněny automaticky.)
import { ICONS, roleHasCompany, auditGroupFilterFieldHtml, auditNoLabel, auditNoValue, cachedList, cachedValue, canEditCorrectiveFields, closedAudits, esc, fmtDate, fmtDateShort, matchesAuditGroup, normalizeSearchText, queueWrite, scopeBadgeHtml, scopeMine, scopeToggleHtml, filterActionsHtml, scopeWorkplaceSet, showToast, sortedAuditTypes, sortedWorkplaces, state, typesInGroup, wpFilterMatchesId, wpFilterFieldsHtml } from '../core/state.js';
import { paginateList, paginationControlsHtml } from '../core/pagination.js';
import { assetsCap, db } from '../core/boot.js';
import { setScopeSilent } from '../core/companies.js';
import { wireDateFilter } from './my-tasks.js';
import { EXTERNAL_RESPONSIBLE, canChangeCorrectiveStatus, externalResponsibleFieldsHtml, isCorrectiveAssignedToMe, responsibleLabelHtml, responsibleUserOptionsHtml, responsiblePersonForWorkplace, userNameById } from './places.js';
import { sortControlHtml } from './history.js';
import { correctiveTableColumns, correctiveTableHtml, detailBackLabel, isTableView, listViewToggleHtml, wireTableDateFilters } from './table.js';
import { dateTimeInputHtml, render } from '../main-render.js';
import { auditUsesOkNok } from './audit-runner.js';
import { auditCorrectiveSeqMap as sharedCorrectiveSeqMap, correctiveCode, hasCorrectiveAction } from '../../supabase/functions/_shared/calc.js';
import { uploadErrorText } from '../core/licence.js';

// ================= OPATŘENÍ (corrective actions overview) =================
// Read overlay over the NOK answers' correctiveAction data already entered during
// audits (description/responsible/dueDate — unchanged, read-only, same as History).
// Status and its change-history are a separate, additional layer stored in their
// own 'correctiveActions' collection, keyed by a composite id derived from the
// audit/answer/sub-answer ids. Nothing here ever writes back to the audits
// collection, so audit records and History stay completely untouched.
var CORRECTIVE_STATUSES = ['v_reseni','hotovo','zruseno'];
var CORRECTIVE_STATUS_LABELS = {v_reseni:'V řešení', hotovo:'Hotovo', zruseno:'Zrušeno'};
var CORRECTIVE_STATUS_RANK = {v_reseni:0, hotovo:1, zruseno:2};
var CORRECTIVE_SORT_OPTIONS = [
  {id:'termin', label:'Termín', defaultDir:'asc'},
  {id:'statusChange', label:'Změna stavu', defaultDir:'desc'},
  {id:'status', label:'Stav', defaultDir:'asc'},
  {id:'closedAt', label:'Datum auditu', defaultDir:'desc'},
  {id:'workplace', label:'Pracoviště', defaultDir:'asc'}
];
function correctiveSortValue(item, sortBy){
  switch(sortBy){
    case 'statusChange':
      var last = (item.history && item.history.length) ? item.history[item.history.length-1].changedAt : item.closedAt;
      return last ? new Date(last).getTime() : 0;
    case 'status':
      return CORRECTIVE_STATUS_RANK[item.status]!=null ? CORRECTIVE_STATUS_RANK[item.status] : 99;
    case 'closedAt':
      return item.closedAt ? new Date(item.closedAt).getTime() : 0;
    case 'workplace':
      return (item.workplaceName||'').toLowerCase();
    case 'termin':
    default:
      return item.dueDate ? new Date(item.dueDate).getTime() : Infinity;
  }
}
function correctiveKey(auditId, ansId, subId){
  return auditId + '::' + ansId + (subId ? ('::'+subId) : '');
}
function correctiveStatusBadgeHtml(status){
  if(status==='hotovo') return '<span class="badge badge-ok">Hotovo</span>';
  if(status==='zruseno') return '<span class="badge badge-neutral">Zrušeno</span>';
  return '<span class="badge badge-na">V řešení</span>';
}
function isCorrectiveOverdue(item){
  if(!item.dueDate || item.status==='hotovo' || item.status==='zruseno') return false;
  return new Date(item.dueDate+'T23:59:59').getTime() < Date.now();
}
// Otevřené opatření, jehož odpovědná osoba nemá licenci Auditu nebo přístup
// k firmě (rozhodnutí uživatele 2026-11-02) — přiřazení se nečistí, jen označí.
function responsibleLacksAccess(item){
  if(!item || !item.responsibleId || item.status==='hotovo' || item.status==='zruseno') return false;
  if(!state.rolesReady) return false;
  var r = state.roles.get(item.responsibleId);
  return !r || !roleHasCompany(r, item.companyId||state.currentCompanyId||'');
}
// ans může být i volné zjištění (a.findings[], isFindingEntry) — má vlastní
// pracoviště a místo otázky jen popis (note).
function buildCorrectiveItem(auditId, audit, ans, sa, ca){
  var key = correctiveKey(auditId, ans.id, sa?sa.id:null);
  var rec = state.correctiveActions.get(key);
  var fnd = !sa && isFindingEntry(ans);
  var wpSrc = (fnd && ans.workplaceId) ? ans : audit;
  return {
    key:key, auditId:auditId, auditNumber:audit.auditNumber||null, auditCode:audit.auditCode||'', answerId:ans.id, subAnswerId: sa?sa.id:null,
    companyId: audit.companyId||'',
    isFinding: fnd,
    workplaceId:wpSrc.workplaceId, workplaceName:wpSrc.workplaceName,
    branchId:wpSrc.branchId||'', branchName:wpSrc.branchName||'Sídlo firmy',
    auditTypeId:audit.auditTypeId, auditTypeName:audit.auditTypeName,
    auditorName:audit.auditorName, auditorId:audit.auditorId||'', closedAt:audit.closedAt,
    questionText: fnd ? '' : (sa ? sa.text : ans.questionText),
    parentQuestionText: sa ? ans.questionText : null,
    description: ca.description||'',
    // responsible/dueDate can be overridden later from the corrective detail
    // screen (App.confirmCorrectiveEdit) — the override lives in the separate
    // correctiveActions/{key} record, the original audit answer is left as-is.
    responsible: (rec && rec.responsible!=null) ? rec.responsible : (ca.responsible||''),
    responsibleId: (rec && rec.responsible!=null) ? (rec.responsibleId||'') : (ca.responsibleId||''),
    // Osoba bez účtu zapsaná ručně (2026-11-03): jen jméno, bez „Moje“ a e-mailů.
    responsibleExternal: (rec && rec.responsible!=null) ? !!rec.responsibleExternal : !!ca.responsibleExternal,
    responsibleFirst: (rec && rec.responsible!=null) ? (rec.responsibleFirst||'') : (ca.responsibleFirst||''),
    responsibleLast: (rec && rec.responsible!=null) ? (rec.responsibleLast||'') : (ca.responsibleLast||''),
    dueDate: (rec && rec.dueDate!=null) ? rec.dueDate : (ca.dueDate||''),
    status: rec ? (rec.status||'v_reseni') : 'v_reseni',
    history: (rec && rec.history) ? rec.history : [],
    attachments: (sa ? sa.attachments : ans.attachments) || [],
    // Zjištění = poznámka auditora u otázky (v detailu opatření „Zjištění“).
    finding: ((sa ? sa.note : ans.note) || '').trim()
  };
}
function allCorrectiveItems(){
  return cachedList('allCorrectiveItems', computeAllCorrectiveItems);
}
function computeAllCorrectiveItems(){
  var out = [];
  closedAudits().forEach(function(e){
    var auditId = e[0], a = e[1], n = 0;
    (a.answers||[]).forEach(function(ans){
      if(hasCorrectiveAction(ans)){
        out.push(withCorrectiveCode(buildCorrectiveItem(auditId, a, ans, null, ans.correctiveAction), a, ++n));
      }
      (ans.subAnswers||[]).forEach(function(sa){
        if(hasCorrectiveAction(sa)){
          out.push(withCorrectiveCode(buildCorrectiveItem(auditId, a, ans, sa, sa.correctiveAction), a, ++n));
        }
      });
    });
    auditFindings(a).forEach(function(f){
      if(hasCorrectiveAction(f)) out.push(withCorrectiveCode(buildCorrectiveItem(auditId, a, f, null, f.correctiveAction), a, ++n));
    });
  });
  return out;
}
// Volná zjištění auditu (a.findings): {id, result 'NOK' (neshoda) | 'GOOD'
// (dobrá praxe), note (popis), attachments, workplaceId/Name, branchId/Name,
// correctiveAction (jen neshoda), createdAt}. Neshoda s opatřením je
// opatření jako NOK otázka — pořadí v ID až za otázkami.
function auditFindings(a){ return (a && Array.isArray(a.findings)) ? a.findings : []; }
// Odpověď na otázku má vždy questionText, zjištění nikdy.
function isFindingEntry(x){ return !!x && typeof x.questionText!=='string'; }
function withCorrectiveCode(item, a, seq){ item.seq = seq; item.code = correctiveCode(a, seq); return item; }
// Hledané ID se shoduje i bez předpony sdíleného druhu („BOZP-12-1“ najde OP-S-BOZP-12-1).
function correctiveCodeMatches(code, cq){ return !!code && (code===cq || code.replace(/^OP-S-/, 'OP-')===cq); }
// Pořadí opatření v auditu: {ansId | ansId::subId -> n} (stejné jako výše).
function auditCorrectiveSeqMap(a){ return sharedCorrectiveSeqMap(a); }
function correctiveCodeOf(a, ansId, subId){
  if(!a || a.auditNumber==null) return '';
  return correctiveCode(a, auditCorrectiveSeqMap(a)[subId ? ansId+'::'+subId : ansId]);
}
function correctiveCodeHtml(code){ return code ? '<span class="corr-code mono">'+esc(code)+'</span>' : ''; }
// Hledání podle ID: "OP-152-2", "op 152-2", "op152/2", "152-2" → přesná shoda.
// „OP-152-2“, „152/2“, „OP-BOZP-12-1“, „bozp 12-1“, „S-BOZP-12-1“ → kanonické ID.
function correctiveCodeQuery(term){
  var t = normalizeSearchText(term).replace(/^\s*op\s*[-_/.]?\s*/, '').trim();
  var m = /^(?:(s)\s*[-_/. ]\s*)?(?:([a-z0-9]{1,5})\s*[-_/. ]\s*)?(\d+)\s*[-_/. ]\s*(\d+)$/.exec(t);
  if(!m) return null;
  if(m[1] && !m[2]){ m[2] = 's'; m[1] = ''; } // „S-12-1“ = kód „S“ (ten nejde založit) — prostě nic nenajde
  return 'OP-'+(m[1]?'S-':'')+(m[2]?m[2].toUpperCase()+'-':'')+parseInt(m[3],10)+'-'+parseInt(m[4],10);
}
// One short status for the whole audit (History list): "Uzavřen" when
// there was nothing to fix (no NOK) or every corrective action is done /
// cancelled, "V řešení" while some are open, "Po termínu" when an open one
// is overdue. A NOK audit with no corrective actions at all (old data)
// shows "NOK" so it can't hide under "Uzavřen". The result itself stays
// visible in the card's OK/NOK/N/A line.
function auditStatus(auditId, a){
  var items = allCorrectiveItems().filter(function(it){ return it.auditId===auditId; });
  var hasNok = !!(a && a.summary && (a.summary.nok>0 || a.summary.overall==='NOK'));
  if(!items.length) return hasNok ? 'nok' : 'closed';
  var open = items.filter(function(it){ return it.status!=='hotovo' && it.status!=='zruseno'; });
  if(!open.length) return 'closed';
  return open.some(isCorrectiveOverdue) ? 'overdue' : 'open';
}
var AUDIT_STATUS_META = {
  closed: {label:'Uzavřen', cls:'badge-ok'},
  open: {label:'V řešení', cls:'badge-na'},
  overdue: {label:'Po termínu', cls:'badge-nok'},
  nok: {label:'NOK', cls:'badge-nok'}
};
// Seznam auditů (Uzavřené): štítek = výsledek auditu (OK/NOK), stav jeho
// opatření je zvlášť textem pod údaji (rozhodnutí uživatele 2026-10-28).
function auditResultBadgeHtml(a){
  if(!a || !a.summary || !auditUsesOkNok(a)) return '';
  var ok = a.summary.overall==='OK';
  return '<span class="badge '+(ok?'badge-ok':'badge-nok')+'">'+(ok?'OK':'NOK')+'</span>';
}
// Opatření podle auditu (mezipaměť jednoho render()).
function correctiveItemsByAudit(){
  return cachedValue('correctiveItemsByAudit', function(){
    var m = {};
    allCorrectiveItems().forEach(function(it){ (m[it.auditId] = m[it.auditId] || []).push(it); });
    return m;
  });
}
// „2 opatření · 1 po termínu“ / „· v řešení“ / „· vyřešena“; nic bez opatření.
function auditCorrectiveLineHtml(auditId, clickable){
  var items = correctiveItemsByAudit()[auditId] || [];
  if(!items.length) return '';
  var open = items.filter(function(it){ return it.status!=='hotovo' && it.status!=='zruseno'; });
  var overdue = open.filter(isCorrectiveOverdue).length;
  var tail = overdue ? '<span style="color:var(--nok);font-weight:600">'+overdue+' po termínu</span>' :
    open.length ? (open.length===items.length ? 'v řešení' : open.length+' v řešení') : 'vyřešena';
  var text = items.length+' opatření · '+tail;
  if(clickable){
    return '<div style="font-size:var(--fs-sm);margin-top:2px"><button type="button" class="link-btn" style="font-size:inherit;padding:0" title="Zobrazit opatření k tomuto auditu" ' +
      'onclick="event.stopPropagation();App.openCorrectiveForAudit(\''+auditId+'\')">'+text+'</button></div>';
  }
  return '<div class="muted" style="font-size:var(--fs-sm);margin-top:2px">'+text+'</div>';
}
function auditStatusBadgeHtml(auditId, a, clickable){
  var st = auditStatus(auditId, a);
  var meta = AUDIT_STATUS_META[st];
  if(clickable && (st==='open' || st==='overdue')){
    return '<button type="button" class="badge '+meta.cls+'" style="border:none;cursor:pointer" title="Zobrazit opatření k tomuto auditu" ' +
      'onclick="event.stopPropagation();App.openCorrectiveForAudit(\''+auditId+'\')">'+meta.label+'</button>';
  }
  return '<span class="badge '+meta.cls+'">'+meta.label+'</span>';
}
// Seznam odpovědných osob / auditorů pro dropdown filtry v Opatřeních —
// jen ti, kdo se skutečně objevují v aktuálních opatřeních, řazeno abecedně.
function correctiveResponsibleOptions(){
  var seen = {}, out = [];
  allCorrectiveItems().forEach(function(it){
    var r = it.responsible;
    if(r && !seen[r]){ seen[r]=true; out.push(r); }
  });
  out.sort(function(a,b){ return a.localeCompare(b,'cs'); });
  return out;
}
function correctiveAuditorOptions(){
  var seen = {}, out = [];
  allCorrectiveItems().forEach(function(it){
    var n = it.auditorName;
    if(n && !seen[n]){ seen[n]=true; out.push(n); }
  });
  out.sort(function(a,b){ return a.localeCompare(b,'cs'); });
  return out;
}
function filteredCorrectiveList(){
  var term = (state.correctiveSearch||'').trim().toLowerCase();
  var from = state.correctiveDateFrom ? new Date(state.correctiveDateFrom+'T00:00:00') : null;
  var to = state.correctiveDateTo ? new Date(state.correctiveDateTo+'T23:59:59') : null;
  // Filtr "opatření k auditu" (proklik z Historie) ukazuje vše k danému auditu.
  // Hledání podle ID opatření (OP-152-2) najde opatření i mimo "Moje".
  var codeQuery = term ? correctiveCodeQuery(term) : null;
  // „Moje“ = jen opatření přímo přidělená mně (rozhodnutí uživatele);
  // opatření na mých pracovištích jsou pod „Vše“ (a v Moje úkoly).
  var mine = scopeMine('corrective') && !state.correctiveAuditFilter && !codeQuery;
  var wpSet = (state.correctiveAuditFilter || codeQuery) ? null : scopeWorkplaceSet('corrective');
  return allCorrectiveItems().filter(function(item){
    if(mine && !isCorrectiveAssignedToMe(item)) return false;
    if(wpSet && !wpSet[item.workplaceId]) return false;
    if(state.correctiveAuditFilter && item.auditId!==state.correctiveAuditFilter) return false;
    if(!wpFilterMatchesId(state.correctiveFilterWp, item.workplaceId)) return false;
    if(state.correctiveFilterType && item.auditTypeId!==state.correctiveFilterType) return false;
    if(state.correctiveFilterGroup && !matchesAuditGroup(item.auditTypeId, state.correctiveFilterGroup)) return false;
    if(state.correctiveFilterStatus && item.status!==state.correctiveFilterStatus) return false;
    if(state.correctiveFilterResponsible && item.responsible!==state.correctiveFilterResponsible) return false;
    if(state.correctiveFilterAuditor && item.auditorName!==state.correctiveFilterAuditor) return false;
    if(state.correctiveOverdueOnly && !isCorrectiveOverdue(item)) return false;
    if(from || to){
      if(!item.dueDate) return false;
      var d = new Date(item.dueDate+'T12:00:00');
      if(from && d<from) return false;
      if(to && d>to) return false;
    }
    if(term){
      if(codeQuery){ if(!correctiveCodeMatches(item.code, codeQuery)) return false; }
      else {
        var hay = normalizeSearchText([item.code, item.workplaceName, item.auditTypeName, item.auditorName, item.responsible, item.description, item.questionText,
          auditNoValue(item)].join(' '));
        if(hay.indexOf(normalizeSearchText(term))<0) return false;
      }
    }
    return true;
  }).sort(function(a,b){
    var sortBy = state.correctiveSortBy || 'termin';
    var av = correctiveSortValue(a, sortBy);
    var bv = correctiveSortValue(b, sortBy);
    var cmp = (typeof av === 'string') ? av.localeCompare(bv, 'cs') : (av - bv);
    if(cmp===0){
      // stable, sensible tie-break: nearest/oldest due date first, then most recently closed
      var ad = a.dueDate ? new Date(a.dueDate).getTime() : Infinity;
      var bd = b.dueDate ? new Date(b.dueDate).getTime() : Infinity;
      cmp = ad - bd;
      if(cmp===0) cmp = new Date(b.closedAt||0) - new Date(a.closedAt||0);
    }
    return state.correctiveSortDir==='desc' ? -cmp : cmp;
  });
}
function renderCorrective(){
  var auditFilterInfo = state.correctiveAuditFilter ? state.audits.get(state.correctiveAuditFilter) : null;
  var html = '';
  if(auditFilterInfo){
    html += '<div class="view-head"><button class="btn btn-sm btn-ghost" style="margin-bottom:10px" onclick="App.backFromCorrectiveAuditFilter()">← Zpět</button>' +
      '<h2>Opatření k auditu</h2><p>'+esc(auditFilterInfo.workplaceName)+' · '+esc(auditFilterInfo.auditTypeName||'Audit')+' · '+fmtDateShort(auditFilterInfo.closedAt)+'</p></div>';
  } else {
    html += '<div class="view-head"><h2>Nápravná opatření</h2><p>Přehled opatření ze všech uzavřených auditů.</p></div>';
  }

  var wps = sortedWorkplaces();
  var types = sortedAuditTypes();
  var responsibles = correctiveResponsibleOptions();
  var auditors = correctiveAuditorOptions();
  var activeFilterCount = (state.correctiveFilterWp?1:0) + (state.correctiveFilterType?1:0) + (state.correctiveFilterGroup?1:0) + (state.correctiveFilterStatus?1:0) +
    (state.correctiveFilterResponsible?1:0) + (state.correctiveFilterAuditor?1:0) +
    (state.correctiveOverdueOnly?1:0) + (state.correctiveDateFrom?1:0) + (state.correctiveDateTo?1:0);

  // list-toolbar: na desktopu jede vyhledávání + Filtry/Řadit v jednom
  // řádku (na mobilu/tabletu zůstávají pod sebou — viz .list-toolbar v CSS).
  html += '<div class="list-toolbar">';
  html += '<div class="field" style="margin-bottom:10px">' +
    '<input id="correctiveSearchInput" value="'+esc(state.correctiveSearch||'')+'" placeholder="Hledat podle ID (OP-…), pracoviště, osoby, popisu…"/></div>';

  html += '<div class="filter-toggle-row">' +
    '<button type="button" class="filter-toggle-btn'+(state.correctiveFiltersOpen?' on':'')+'" onclick="App.toggleCorrectiveFiltersOpen()">'+ICONS.filter+' Filtry'+(state.correctiveAuditFilter?'':scopeBadgeHtml('corrective'))+
    (activeFilterCount>0 ? '<span class="count-badge">'+activeFilterCount+'</span>' : '')+
    '</button>' +
    (isTableView('corrective') ? '' : sortControlHtml('correctiveSortSelect', CORRECTIVE_SORT_OPTIONS, state.correctiveSortBy, state.correctiveSortDir, 'toggleCorrectiveSortDir')) +
    listViewToggleHtml('corrective') +
    '</div>';
  html += '</div>';

  if(state.correctiveFiltersOpen){
    html += '<div class="filter-panel">' + (state.correctiveAuditFilter && state.view==='corrective' ? '' : scopeToggleHtml('corrective'));
    html += '<div class="filter-grid" style="margin-bottom:10px">' +
      wpFilterFieldsHtml('correctiveWpSelect', state.correctiveFilterWp, wps, 'App.setCorrectiveWpFilter(%s)') +
      auditGroupFilterFieldHtml('correctiveGroupSelect', state.correctiveFilterGroup) +
      '<div class="field"><label>Druh auditu</label>' +
      '<select class="filter-select" id="correctiveTypeSelect" '+(types.length===0?'disabled':'')+'><option value="">Všechny druhy</option>' +
      typesInGroup(types, state.correctiveFilterGroup).map(function(e){ return '<option value="'+e[0]+'" '+(state.correctiveFilterType===e[0]?'selected':'')+'>'+esc(e[1].name)+'</option>'; }).join('') +
      '</select></div>' +
      '<div class="field"><label>Odpovědná osoba</label>' +
      '<select class="filter-select" id="correctiveResponsibleSelect" '+(responsibles.length===0?'disabled':'')+'><option value="">Všechny osoby</option>' +
      responsibles.map(function(n){ return '<option value="'+esc(n)+'" '+(state.correctiveFilterResponsible===n?'selected':'')+'>'+esc(n)+'</option>'; }).join('') +
      '</select></div>' +
      '<div class="field"><label>Auditor</label>' +
      '<select class="filter-select" id="correctiveAuditorSelect" '+(auditors.length===0?'disabled':'')+'><option value="">Všichni auditoři</option>' +
      auditors.map(function(n){ return '<option value="'+esc(n)+'" '+(state.correctiveFilterAuditor===n?'selected':'')+'>'+esc(n)+'</option>'; }).join('') +
      '</select></div></div>';

    html += '<div style="margin-bottom:10px"><label>Stav</label>' +
      '<div class="pill-select" style="margin-bottom:0;margin-top:4px">' +
      '<button type="button" class="'+(state.correctiveFilterStatus===''?'on':'')+'" onclick="App.setCorrectiveStatusFilter(\'\')">Vše</button>' +
      CORRECTIVE_STATUSES.map(function(s){ return '<button type="button" class="'+(state.correctiveFilterStatus===s?'on':'')+'" onclick="App.setCorrectiveStatusFilter(\''+s+'\')">'+CORRECTIVE_STATUS_LABELS[s]+'</button>'; }).join('') +
      '</div></div>';

    html += '<div style="margin-bottom:10px"><div class="pill-select" style="margin-bottom:0">' +
      '<button type="button" class="'+(state.correctiveOverdueOnly?'on':'')+'" onclick="App.toggleCorrectiveOverdueOnly()">Jen po termínu</button>' +
      '</div></div>';

    html += '<div class="field-row" style="margin-bottom:0">' +
      '<div class="field field-date" style="margin-bottom:0"><label>Termín od</label>' +
      '<div class="date-wrap">'+dateTimeInputHtml({id:'correctiveDateFrom', value:state.correctiveDateFrom}) +
      '</div></div>' +
      '<div class="field field-date" style="margin-bottom:0"><label>Termín do</label>' +
      '<div class="date-wrap">'+dateTimeInputHtml({id:'correctiveDateTo', value:state.correctiveDateTo}) +
      '</div></div></div>';
    html += filterActionsHtml('clearCorrectiveFilters', 'corrective', state.correctiveFilterStatus==='v_reseni' && !state.correctiveFilterWp && !state.correctiveFilterType && !state.correctiveFilterGroup && !state.correctiveFilterResponsible && !state.correctiveFilterAuditor && !state.correctiveOverdueOnly && !state.correctiveDateFrom && !state.correctiveDateTo);
    html += '</div>';
  }

  var list = filteredCorrectiveList();
  var correctiveSig = JSON.stringify([state.correctiveFilterWp, state.correctiveFilterType, state.correctiveFilterGroup, state.correctiveFilterStatus,
    state.correctiveFilterResponsible, state.correctiveFilterAuditor,
    state.correctiveOverdueOnly, state.correctiveDateFrom, state.correctiveDateTo, state.correctiveSearch,
    state.correctiveSortBy, state.correctiveSortDir, state.correctiveAuditFilter]);
  if(isTableView('corrective')){
    if(state.correctiveDetailId){
      var tdHtml = correctiveDetailHtml(state.correctiveDetailId);
      if(tdHtml!=null){ document.getElementById('content').innerHTML = '<div class="table-detail-page">'+tdHtml+'</div>'; return; }
    }
    html += correctiveTableHtml(list, correctiveSig);
    document.getElementById('content').innerHTML = html;
    wireCorrectiveToolbar();
    wireTableDateFilters('corrective', correctiveTableColumns());
    return;
  }
  var page = paginateList(list, 'correctivePage', 'correctivePageSig', correctiveSig);
  var visibleList = page.items;
  if(list.length===0){
    html += '<div class="empty">'+ICONS.empty+'<p>Žádná opatření neodpovídají filtru.</p></div>';
  } else {
    html += '<div class="card">';
    visibleList.forEach(function(item,i){
      var overdue = isCorrectiveOverdue(item);
      html += (i>0?'<div class="list-sep"></div>':'');
      html += '<button class="list-item" style="width:100%;background:none;border:none;text-align:left;cursor:pointer;padding:2px;align-items:flex-start" onclick="App.openCorrectiveDetail(\''+item.key+'\')">' +
        '<div style="min-width:0;flex:1">' +
        (item.code ? '<div style="margin-bottom:3px">'+correctiveCodeHtml(item.code)+'</div>' : '') +
        '<div style="font-weight:600;font-size:var(--fs-md)">'+esc(item.description||'—')+'</div>' +
        '<div class="muted" style="font-size:var(--fs-sm);margin-top:2px">'+esc(item.workplaceName)+' · '+esc(item.branchName||'Sídlo firmy')+
        (item.auditNumber!=null && !item.code ? ' · <span class="mono">audit '+esc(auditNoLabel(item))+'</span>' : '')+'</div>' +
        '<div class="muted" style="font-size:var(--fs-sm);margin-top:2px">'+(item.responsible?responsibleLabelHtml(item.responsible, item.responsibleExternal):'bez odpovědné osoby')+
        (item.dueDate ? ' · termín <span style="'+(overdue?'color:var(--nok);font-weight:700':'')+'">'+fmtDateShort(item.dueDate)+'</span>' : '') + '</div>' +
        '</div>' +
        '<div style="display:flex;flex-direction:column;align-items:flex-end;gap:4px;flex-shrink:0">' +
        correctiveStatusBadgeHtml(item.status) +
        (overdue ? '<span class="badge badge-nok">Po termínu</span>' : '') +
        '</div></button>';
    });
    html += '</div>';
    html += paginationControlsHtml('correctivePage', page);
  }

  // Split-view pilot rozšířený i na Opatření (viz Historie auditů výše):
  // na desktopu seznam a detail vedle sebe, na mobilu/tabletu beze změny.
  var hasDetail = !!state.correctiveDetailId;
  var detailHtml = hasDetail ? correctiveDetailHtml(state.correctiveDetailId) : null;
  if(hasDetail && detailHtml==null){
    renderCorrective();
    return;
  }
  if(!hasDetail){
    detailHtml = '<div class="split-detail-empty"><p class="muted">Vyberte opatření ze seznamu.</p></div>';
  }
  html = '<div class="split-view'+(hasDetail?' has-detail':'')+'"><div class="split-list">' + html + '</div>' +
    '<div class="split-detail">' + detailHtml + '</div></div>';
  document.getElementById('content').innerHTML = html;
  wireCorrectiveToolbar();
}
function wireCorrectiveToolbar(){
  var searchEl = document.getElementById('correctiveSearchInput');
  if(searchEl) searchEl.addEventListener('input', function(){ state.correctiveSearch = searchEl.value; render(); });
  var fromEl = document.getElementById('correctiveDateFrom');
  wireDateFilter(fromEl, 'correctiveDateFrom');
  var toEl = document.getElementById('correctiveDateTo');
  wireDateFilter(toEl, 'correctiveDateTo');
  var wpSelEl = document.getElementById('correctiveWpSelect');
  if(wpSelEl) wpSelEl.addEventListener('change', function(){ App.setCorrectiveWpFilter(wpSelEl.value); });
  var typeSelEl = document.getElementById('correctiveTypeSelect');
  if(typeSelEl) typeSelEl.addEventListener('change', function(){ App.setCorrectiveTypeFilter(typeSelEl.value); });
  var groupSelEl = document.getElementById('correctiveGroupSelect');
  if(groupSelEl) groupSelEl.addEventListener('change', function(){ App.setCorrectiveGroupFilter(groupSelEl.value); });
  var respSelEl = document.getElementById('correctiveResponsibleSelect');
  if(respSelEl) respSelEl.addEventListener('change', function(){ App.setCorrectiveResponsibleFilter(respSelEl.value); });
  var corAuditorSelEl = document.getElementById('correctiveAuditorSelect');
  if(corAuditorSelEl) corAuditorSelEl.addEventListener('change', function(){ App.setCorrectiveAuditorFilter(corAuditorSelEl.value); });
  var sortSelEl = document.getElementById('correctiveSortSelect');
  if(sortSelEl) sortSelEl.addEventListener('change', function(){ App.setCorrectiveSortBy(sortSelEl.value); });
}
App.setCorrectiveSortBy = function(v){
  var opt = CORRECTIVE_SORT_OPTIONS.find(function(o){ return o.id===v; });
  state.correctiveSortBy = v;
  state.correctiveSortDir = opt ? opt.defaultDir : 'asc';
  render();
};
App.toggleCorrectiveSortDir = function(){
  state.correctiveSortDir = state.correctiveSortDir==='asc' ? 'desc' : 'asc';
  render();
};
App.openCorrectiveForAudit = function(auditId){
  state.correctiveAuditFilter = auditId;
  state.correctiveFilterStatus = '';
  state.correctiveDetailId = null;
  state.view = 'corrective';
  render();
};
App.backFromCorrectiveAuditFilter = function(){
  state.correctiveAuditFilter = null;
  App.setView('history');
};
App.toggleCorrectiveFiltersOpen = function(){ state.correctiveFiltersOpen = !state.correctiveFiltersOpen; render(); };
App.setCorrectiveWpFilter = function(v){ state.correctiveFilterWp = v; render(); };
App.setCorrectiveTypeFilter = function(v){ state.correctiveFilterType = v; render(); };
App.setCorrectiveGroupFilter = function(v){
  state.correctiveFilterGroup = v;
  if(v && state.correctiveFilterType && !matchesAuditGroup(state.correctiveFilterType, v)) state.correctiveFilterType = '';
  render();
};
App.setCorrectiveStatusFilter = function(v){ state.correctiveFilterStatus = v; render(); };
App.setCorrectiveResponsibleFilter = function(v){ state.correctiveFilterResponsible = v; render(); };
App.setCorrectiveAuditorFilter = function(v){ state.correctiveFilterAuditor = v; render(); };
App.toggleCorrectiveOverdueOnly = function(){ state.correctiveOverdueOnly = !state.correctiveOverdueOnly; render(); };
// Z Moje úkoly ("+ N dalších"): Opatření — moje, jen po termínu, odpovědná
// osoba = já (filtr zabalený).
App.openMyOverdueCorrective = function(){
  state.correctiveFilterWp = '';
  state.correctiveFilterType = ''; state.correctiveFilterGroup = '';
  state.correctiveFilterStatus = '';
  state.correctiveFilterResponsible = userNameById(state.myId);
  state.correctiveFilterAuditor = '';
  state.correctiveOverdueOnly = true;
  state.correctiveDateFrom = '';
  state.correctiveDateTo = '';
  state.correctiveSearch = '';
  state.correctiveAuditFilter = '';
  state.correctiveDetailId = null;
  state.correctiveFiltersOpen = false;
  setScopeSilent('corrective', 'mine');
  state.correctivePage = 1;
  state.view = 'corrective';
  render();
};
// toDefault: výchozí nastavení (Moje + V řešení), jinak vše zrušit (Vše).
App.clearCorrectiveFilters = function(toDefault){
  state.correctiveFilterWp = '';
  state.correctiveFilterType = ''; state.correctiveFilterGroup = '';
  state.correctiveFilterStatus = toDefault ? 'v_reseni' : '';
  setScopeSilent('corrective', toDefault ? 'mine' : 'all');
  state.correctiveFilterResponsible = '';
  state.correctiveFilterAuditor = '';
  state.correctiveOverdueOnly = false;
  state.correctiveDateFrom = '';
  state.correctiveDateTo = '';
  render();
};
// returnTo (optional) remembers where to go back to instead of the
// "Opatření" list — e.g. {view:'history', historyDetailId:auditId} when
// opened from a status badge inside a closed audit's detail screen, so
// "Zpět" lands back on that audit instead of the generic opatření list.
App.openCorrectiveDetail = function(key, returnTo){
  state.view = 'corrective';
  state.correctiveDetailId = key;
  state.correctiveDetailReturnTo = returnTo || null;
  state.correctivePendingStatus = null;
  state.correctivePendingNote = '';
  state.correctivePendingAttachments = [];
  state.correctiveEditOpen = false;
  render();
};
App.backFromCorrectiveDetail = function(){
  var returnTo = state.correctiveDetailReturnTo;
  state.correctiveDetailId = null;
  state.correctiveDetailReturnTo = null;
  state.correctivePendingStatus = null;
  state.correctivePendingNote = '';
  state.correctivePendingAttachments = [];
  state.correctiveEditOpen = false;
  if(returnTo && returnTo.view==='history' && returnTo.historyDetailId){
    state.view = 'history';
    state.historyDetailId = returnTo.historyDetailId;
  }
  render();
};
App.startCorrectiveStatusChange = function(status){
  var item = findCorrectiveItemByKey(state.correctiveDetailId);
  if(!item || !canChangeCorrectiveStatus(item)) return;
  state.correctivePendingStatus = status;
  state.correctivePendingNote = '';
  state.correctivePendingAttachments = [];
  render();
};
App.cancelCorrectiveStatusChange = function(){
  state.correctivePendingStatus = null;
  state.correctivePendingNote = '';
  state.correctivePendingAttachments = [];
  render();
};
App.setCorrectivePendingNoteLocal = function(val){
  state.correctivePendingNote = val;
};
App.handleCorrectiveStatusPhoto = async function(ev){
  var files = Array.from(ev.target.files||[]);
  ev.target.value = '';
  if(!files.length || !assetsCap) { if(!assetsCap) showToast('Nahrávání příloh není dostupné.'); return; }
  for(var i=0;i<files.length;i++){
    try{
      var res = await assetsCap.upload(files[i]);
      state.correctivePendingAttachments = state.correctivePendingAttachments || [];
      state.correctivePendingAttachments.push({id:res.id, url:res.url, contentType:res.contentType});
      render();
    }catch(e){
      console.error('upload failed', e);
      showToast(uploadErrorText(e, 'Nahrání fotky se nezdařilo.'));
    }
  }
};
// Přílohy opatření (rozhodnutí uživatele 2026-11-01): fotky i soubory (PDF,
// Word, Excel…). Obrázek = náhled, jiný soubor = štítek s názvem (otevře/stáhne).
var CORRECTIVE_FILE_MAX = 20 * 1024 * 1024;
function isImageAttachment(att){
  var ct = String((att && att.contentType) || '');
  if(ct) return ct.indexOf('image/')===0;
  return /\.(jpe?g|png|gif|webp|heic|heif)$/i.test(String((att && (att.name || att.id)) || ''));
}
function fileSizeLabel(n){
  if(!(n > 0)) return '';
  return n < 1024*1024 ? Math.max(1, Math.round(n/1024))+' kB' : (n/1024/1024).toFixed(1).replace('.', ',')+' MB';
}
function attachmentName(att){
  return (att && att.name) || String((att && att.id) || '').replace(/^.*\/\d+_[a-z0-9]+_/i, '') || 'soubor';
}
// removeFn = název funkce App.* pro odebrání (rozpracovaný záznam), jinak jen zobrazení.
function attachmentsRowHtml(list, removeFn){
  return (list||[]).map(function(att){
    var rm = removeFn ? '<button class="rm" onclick="App.'+removeFn+'(\''+esc(att.id)+'\')">×</button>' : '';
    if(isImageAttachment(att)){
      return '<div class="photo-thumb"><img loading="lazy" decoding="async" src="'+esc(att.url)+'" onclick="App.viewPhoto(\''+esc(att.url)+'\')"/>'+rm+'</div>';
    }
    return '<div class="file-chip"><a href="'+esc(att.url)+'" target="_blank" rel="noopener" download="'+esc(attachmentName(att))+'" title="'+esc(attachmentName(att))+'">' +
      ICONS.fileDoc+'<span class="file-chip-name">'+esc(attachmentName(att))+'</span>' +
      (att.size ? '<span class="file-chip-size">'+fileSizeLabel(att.size)+'</span>' : '')+'</a>'+rm+'</div>';
  }).join('');
}
App.handleCorrectiveStatusFile = async function(ev){
  var files = Array.from(ev.target.files||[]);
  ev.target.value = '';
  if(!files.length || !assetsCap){ if(!assetsCap) showToast('Nahrávání příloh není dostupné.'); return; }
  for(var i=0;i<files.length;i++){
    if(files[i].size > CORRECTIVE_FILE_MAX){ showToast('Soubor '+files[i].name+' je větší než 20 MB.'); continue; }
    try{
      var res = await assetsCap.upload(files[i]);
      state.correctivePendingAttachments = state.correctivePendingAttachments || [];
      state.correctivePendingAttachments.push({id:res.id, url:res.url, contentType:res.contentType || files[i].type || '', name:files[i].name || '', size:files[i].size || 0});
      render();
    }catch(e){
      console.error('upload failed', e);
      showToast(uploadErrorText(e, 'Nahrání souboru se nezdařilo.'));
    }
  }
};
App.removeCorrectivePendingPhoto = function(attId){
  state.correctivePendingAttachments = (state.correctivePendingAttachments||[]).filter(function(a){return a.id!==attId;});
  render();
  if(assetsCap){ assetsCap.delete(attId).catch(function(){}); }
};
App.confirmCorrectiveStatusChange = function(key){
  if(!db || !state.correctivePendingStatus) return;
  var itemForCheck = findCorrectiveItemByKey(key);
  if(!itemForCheck || !canChangeCorrectiveStatus(itemForCheck)) return;
  var newStatus = state.correctivePendingStatus;
  var note = (state.correctivePendingNote||'').trim();
  var attachments = (state.correctivePendingAttachments||[]).slice();
  var sameStatus = newStatus===itemForCheck.status;
  if(sameStatus && !note && !attachments.length){ showToast('Doplňte poznámku, fotku nebo soubor.'); return; }
  var existing = state.correctiveActions.get(key);
  var history = (existing && existing.history) ? existing.history.slice() : [];
  var entry = {status:newStatus, changedBy: state.meName || state.meEmail || '—', changedAt: new Date().toISOString(), note: note};
  if(attachments.length) entry.attachments = attachments;
  history.push(entry);
  queueWrite(function(){
    return db.doc('correctiveActions/'+key).update({status:newStatus, history:history}).then(function(){
      showToast(sameStatus ? 'Záznam uložen.' : 'Stav opatření změněn.');
      state.correctivePendingStatus = null;
      state.correctivePendingNote = '';
      state.correctivePendingAttachments = [];
      render();
    });
  });
};
// ---- edit responsible person / due date on an already-closed corrective
// action. The original audit answer (correctiveAction.responsible/dueDate)
// is left untouched for history; this writes an override into the separate
// correctiveActions/{key} record, same place the status lives, and logs the
// change (who/when/what) into that record's shared history list. ----
App.toggleCorrectiveEditForm = function(){
  if(!canEditCorrectiveFields(findCorrectiveItemByKey(state.correctiveDetailId))) return;
  state.correctiveEditOpen = !state.correctiveEditOpen;
  if(state.correctiveEditOpen){
    var item = findCorrectiveItemByKey(state.correctiveDetailId);
    state.correctiveEditResponsible = item ? (item.responsibleExternal ? EXTERNAL_RESPONSIBLE : (item.responsibleId||'')) : '';
    state.correctiveEditFirst = item && item.responsibleExternal ? (item.responsibleFirst || (item.responsible||'').split(' ')[0] || '') : '';
    state.correctiveEditLast = item && item.responsibleExternal ? (item.responsibleLast || (item.responsible||'').split(' ').slice(1).join(' ')) : '';
    state.correctiveEditDueDate = item ? (item.dueDate||'') : '';
  }
  render();
};
App.cancelCorrectiveEdit = function(){
  state.correctiveEditOpen = false;
  render();
};
App.setCorrectiveEditResponsibleLocal = function(val){
  var wasExt = state.correctiveEditResponsible===EXTERNAL_RESPONSIBLE;
  state.correctiveEditResponsible = val;
  if(wasExt !== (val===EXTERNAL_RESPONSIBLE)) render(); // ukázat / skrýt pole jména
};
App.setCorrectiveEditExternalLocal = function(part, val){
  if(part==='first') state.correctiveEditFirst = val; else state.correctiveEditLast = val;
};
App.setCorrectiveEditDueDateLocal = function(val){ state.correctiveEditDueDate = val; };
App.confirmCorrectiveEdit = function(key){
  var item = findCorrectiveItemByKey(key);
  if(!db || !item || !canEditCorrectiveFields(item)) return;
  var ext = state.correctiveEditResponsible===EXTERNAL_RESPONSIBLE;
  var first = (state.correctiveEditFirst||'').trim(), last = (state.correctiveEditLast||'').trim();
  var newResponsibleId = ext ? '' : (state.correctiveEditResponsible||'');
  var newResponsible = ext ? (first+' '+last).trim() : userNameById(newResponsibleId);
  var newDueDate = state.correctiveEditDueDate || '';
  if(ext && (!first || !last)){ showToast('Zapište jméno i příjmení odpovědné osoby.'); return; }
  if(!ext && (!newResponsibleId || !newResponsible)){ showToast('Vyberte odpovědnou osobu.'); return; }
  var oldResponsible = item.responsible||'', oldDueDate = item.dueDate||'';
  if(newResponsibleId===(item.responsibleId||'') && newResponsible===oldResponsible && ext===!!item.responsibleExternal && newDueDate===oldDueDate){
    state.correctiveEditOpen = false; render(); return;
  }
  var changes = [];
  if(newResponsible!==oldResponsible) changes.push('odpovědná osoba: '+(oldResponsible||'—')+' → '+newResponsible);
  if(newDueDate!==oldDueDate) changes.push('termín: '+(oldDueDate?fmtDateShort(oldDueDate):'—')+' → '+(newDueDate?fmtDateShort(newDueDate):'—'));
  var existing = state.correctiveActions.get(key);
  var history = (existing && existing.history) ? existing.history.slice() : [];
  history.push({type:'edit', changedBy: state.meName || state.meEmail || '—', changedAt: new Date().toISOString(), note: changes.join('; ')});
  queueWrite(function(){
    return db.doc('correctiveActions/'+key).update({responsible:newResponsible, responsibleId:newResponsibleId,
      responsibleExternal: ext ? true : undefined, responsibleFirst: ext ? first : undefined, responsibleLast: ext ? last : undefined,
      dueDate:newDueDate, history:history}).then(function(){
      showToast('Opatření aktualizováno.');
      state.correctiveEditOpen = false;
      render();
    });
  });
};
function findCorrectiveItemByKey(key){
  var found = null;
  allCorrectiveItems().some(function(item){ if(item.key===key){ found=item; return true; } return false; });
  return found;
}
// Vrací HTML detailu opatření jako string (nezapisuje do DOM) — stejný
// princip jako historyDetailHtml() pro split-view pilot. Vrátí null při
// neplatném klíči; volající (renderCorrective()) pak sám klíč vynuluje
// a znovu vykreslí.
function correctiveDetailHtml(key){
  var item = findCorrectiveItemByKey(key);
  if(!item){ state.correctiveDetailId=null; return null; }
  var overdue = isCorrectiveOverdue(item);
  // Editing who's responsible / the due date is content management
  // (Administrátor/Správce); changing the status is allowed for whoever
  // this opatření is actually assigned to as well (role Uživatel, only on
  // their own items) — see canChangeCorrectiveStatus.
  var canEditFields = canEditCorrectiveFields(item);
  var canChangeStatus = canChangeCorrectiveStatus(item);
  var isDesktop = !!(window.matchMedia && window.matchMedia('(min-width:1024px)').matches);
  var html = '<div class="view-head"><div class="row-between" style="margin-bottom:10px;flex-wrap:wrap;gap:8px">' +
    '<button class="btn btn-sm btn-ghost" onclick="App.backFromCorrectiveDetail()">'+detailBackLabel('corrective')+'</button>' +
    '<div style="display:flex;gap:6px;flex-wrap:wrap">' +
    '<button id="correctivePdfBtn" class="btn btn-sm" onclick="App.exportCorrectiveItemPdf(\''+key+'\')">'+ICONS.download+' PDF</button>' +
    '<button class="btn btn-sm" onclick="App.openHistoryDetail(\''+item.auditId+'\',{view:\'corrective\',correctiveDetailId:\''+key+'\'})">Otevřít audit</button>' +
    '</div></div>' +
    '<h2 style="font-size:var(--fs-2xl)">'+esc(item.workplaceName)+
    (item.auditNumber!=null ? ' <span class="muted mono" style="font-size:var(--fs-lg);font-weight:400">'+esc(auditNoLabel(item))+'</span>' : '')+
    '</h2><p>'+esc(item.auditTypeName||'Audit')+' · '+fmtDateShort(item.closedAt)+'</p></div>';

  html += '<div class="card detail-head corr-card">';
  // Karta opatření (stejné rozložení na všech zařízeních): nahoře ID a stav,
  // pak co je potřeba udělat, pak kdo a do kdy (s tlačítkem pro změnu
  // přímo u těchto údajů) a nakonec souvislosti z auditu.
  html += '<div class="corr-card-top">' + (item.code ? correctiveCodeHtml(item.code) : '<span></span>') +
    '<div class="corr-card-badges">' + correctiveStatusBadgeHtml(item.status) +
    (overdue ? '<span class="badge badge-nok">Po termínu</span>' : '') + '</div></div>';
  html += '<div class="corr-card-desc">'+esc(item.description||'—')+'</div>';
  if(canEditFields && state.correctiveEditOpen){
    html += '<div class="corr-assign corr-assign-edit">' +
      '<div style="font-size:var(--fs-base);font-weight:600;margin-bottom:8px">Změnit osobu nebo termín</div>' +
      '<div class="field" style="margin-bottom:8px"><label>Odpovědná osoba</label>' +
      '<select id="correctiveEditResponsible" onchange="App.setCorrectiveEditResponsibleLocal(this.value)">'+responsibleUserOptionsHtml(state.correctiveEditResponsible===EXTERNAL_RESPONSIBLE ? '' : (state.correctiveEditResponsible||''), null, {external:true, externalSelected: state.correctiveEditResponsible===EXTERNAL_RESPONSIBLE})+'</select></div>' +
      (state.correctiveEditResponsible===EXTERNAL_RESPONSIBLE ? externalResponsibleFieldsHtml(state.correctiveEditFirst, state.correctiveEditLast, 'App.setCorrectiveEditExternalLocal(', null, 'corrEditExt') : '') +
      '<div class="field" style="margin-bottom:10px"><label>Termín</label>' +
      dateTimeInputHtml({id:'correctiveEditDueDate', value:state.correctiveEditDueDate, onChange:'App.setCorrectiveEditDueDateLocal(this.value)'}) + '</div>' +
      '<div style="display:flex;gap:8px;flex-wrap:wrap">' +
      '<button class="btn btn-sm btn-primary" onclick="App.confirmCorrectiveEdit(\''+key+'\')">Uložit</button>' +
      '<button class="btn btn-sm btn-ghost" onclick="App.cancelCorrectiveEdit()">Zrušit</button>' +
      '</div></div>';
  } else {
    html += '<div class="corr-assign"><div class="corr-assign-inner"><dl class="kv">' +
      '<dt>Odpovědná osoba</dt><dd>'+(item.responsible ? '<b>'+esc(item.responsible)+'</b>'+(item.responsibleExternal ? ' <span class="badge badge-neutral">bez účtu</span>' : '') : '<span class="muted">neurčena</span>') +
        (responsibleLacksAccess(item) ? ' <span class="badge badge-nok" title="Odpovědná osoba nemá licenci Auditu nebo přístup k této firmě — předejte opatření jinému.">Bez přístupu</span>' : '')+'</dd>' +
      '<dt>Termín</dt><dd>'+(item.dueDate ? '<span style="'+(overdue?'color:var(--nok);font-weight:700':'font-weight:600')+'">'+fmtDateShort(item.dueDate)+'</span>' : '<span class="muted">neurčen</span>')+'</dd>' +
      '</dl>' +
      (canEditFields ? '<button type="button" class="btn btn-sm corr-assign-btn" onclick="App.toggleCorrectiveEditForm()">'+ICONS.edit+' Změnit osobu nebo termín</button>' : '') +
      '</div></div>';
  }
  // Odpovědná osoba za pracoviště = dnešní nastavení pracoviště (Pracoviště
  // a osoby), ne údaj z auditu.
  var wpRpName = userNameById(responsiblePersonForWorkplace(item.workplaceId));
  var openAuditJs = 'App.openHistoryDetail(\''+item.auditId+'\',{view:\'corrective\',correctiveDetailId:\''+key+'\'})';
  html += '<div class="corr-section-title">Z auditu</div><dl class="kv">' +
    (item.auditNumber!=null ? '<dt>Číslo auditu</dt><dd><button type="button" class="link-btn corr-audit-link mono" title="Otevřít audit" onclick="'+openAuditJs+'">'+esc(auditNoLabel(item))+'</button></dd>' : '') +
    '<dt>Provozovna</dt><dd>'+esc(item.branchName||'Sídlo firmy')+'</dd>' +
    '<dt>Odpovídá za pracoviště</dt><dd>'+(wpRpName ? esc(wpRpName) : '<span class="muted">neurčeno</span>')+'</dd>' +
    (item.isFinding ? '<dt>Otázka</dt><dd class="muted">Volné zjištění (bez otázky)</dd>' :
      '<dt>Otázka</dt><dd>'+(item.parentQuestionText ? esc(item.parentQuestionText)+' → ' : '')+esc(item.questionText)+'</dd>') +
    (item.finding && item.finding!==item.description ? '<dt>Zjištění</dt><dd class="corr-finding">'+esc(item.finding)+'</dd>' : '') +
    '<dt>Auditor</dt><dd>'+esc(item.auditorName||'—')+'</dd>' +
    '</dl>';
  if(item.attachments && item.attachments.length){
    html += '<div style="margin-top:10px"><div class="muted" style="font-size:var(--fs-sm);margin-bottom:6px">Fotky z auditu</div>' +
      '<div class="photo-row">' + item.attachments.map(function(att){
        return '<div class="photo-thumb"><img loading="lazy" decoding="async" src="'+att.url+'" onclick="App.viewPhoto(\''+att.url+'\')"/></div>';
      }).join('') + '</div></div>';
  }
  html += '</div>';

  if(canChangeStatus){
    html += '<div class="card" style="margin-bottom:14px">' +
      '<div style="font-size:var(--fs-base);font-weight:600;margin-bottom:8px">Změnit stav</div>' +
      '<div class="pill-select corr-status-pills" style="margin-bottom:0">' +
      CORRECTIVE_STATUSES.map(function(s){ return '<button type="button" class="'+(item.status===s?'on':'')+'" onclick="App.startCorrectiveStatusChange(\''+s+'\')">'+CORRECTIVE_STATUS_LABELS[s]+'</button>'; }).join('') +
      '</div>';
    // Klik na aktuální stav = doplnit záznam k tomuto stavu (poznámka nebo
    // fotka o postupu řešení), stav se nemění (rozhodnutí uživatele).
    if(state.correctivePendingStatus){
      var sameStatus = state.correctivePendingStatus===item.status;
      html += '<div style="margin-top:10px;padding:10px;border-radius:8px;background:var(--accent-soft);border:1px solid var(--accent)">' +
        '<div style="font-size:var(--fs-base);margin-bottom:6px">'+(sameStatus ? 'Doplnit záznam ke stavu' : 'Změnit stav na:')+' <b>'+CORRECTIVE_STATUS_LABELS[state.correctivePendingStatus]+'</b></div>' +
        '<div class="field" style="margin-bottom:8px"><textarea id="correctiveStatusNote" rows="2" placeholder="'+(sameStatus ? 'Co je nového (např. objednán materiál)' : 'Poznámka ke změně (nepovinné)')+'" oninput="App.setCorrectivePendingNoteLocal(this.value)">'+esc(state.correctivePendingNote||'')+'</textarea></div>' +
        '<div style="margin-bottom:8px"><div class="muted" style="font-size:var(--fs-sm);margin-bottom:6px">Fotka nebo soubor jako doklad (nepovinné)</div>' +
        '<div class="photo-row">' +
        attachmentsRowHtml(state.correctivePendingAttachments, 'removeCorrectivePendingPhoto') +
        '<label class="photo-add" title="Přidat fotku">'+ICONS.camera +
        '<input type="file" accept="image/*" capture="environment" multiple style="display:none" onchange="App.handleCorrectiveStatusPhoto(event)"/></label>' +
        '<label class="photo-add" id="corrFileAdd" title="Přidat soubor (PDF, Word, Excel…)">'+ICONS.paperclip +
        '<input type="file" multiple style="display:none" onchange="App.handleCorrectiveStatusFile(event)"/></label>' +
        '</div></div>' +
        '<div style="display:flex;gap:8px">' +
        '<button class="btn btn-sm btn-primary" onclick="App.confirmCorrectiveStatusChange(\''+key+'\')">Uložit</button>' +
        '<button class="btn btn-sm btn-ghost" onclick="App.cancelCorrectiveStatusChange()">Zrušit</button>' +
        '</div></div>';
    }
    html += '</div>';
  }

  html += '<div class="card"><div style="font-size:var(--fs-base);font-weight:600;margin-bottom:8px">Historie změn</div>';
  if(!item.history || item.history.length===0){
    html += '<div class="muted" style="font-size:var(--fs-base)">Stav zatím nebyl změněn.</div>';
  } else {
    var chPage = paginateList(item.history.slice().reverse(), 'corrHistPage', 'corrHistPageSig', key);
    chPage.items.forEach(function(h,i){
      html += (i>0?'<div style="border-top:1px solid var(--border);margin:8px 0"></div>':'') +
        '<div>'+(h.type==='edit' ? '<span class="badge badge-neutral">Úprava</span>' : correctiveStatusBadgeHtml(h.status))+' <span class="muted" style="font-size:var(--fs-sm)">'+fmtDate(h.changedAt)+' · '+esc(h.changedBy||'—')+'</span>' +
        (h.note ? '<div style="font-size:var(--fs-base);margin-top:4px">'+esc(h.note)+'</div>' : '') +
        (h.attachments && h.attachments.length ? '<div class="photo-row" style="margin-top:6px">' + attachmentsRowHtml(h.attachments) + '</div>' : '') +
        '</div>';
    });
    html += paginationControlsHtml('corrHistPage', chPage);
  }
  html += '</div>';

  return html;
}

export {
  auditFindings,
  isFindingEntry,
  auditCorrectiveLineHtml,
  auditResultBadgeHtml,
  AUDIT_STATUS_META,
  CORRECTIVE_STATUS_LABELS,
  CORRECTIVE_STATUS_RANK,
  allCorrectiveItems,
  auditCorrectiveSeqMap,
  auditStatusBadgeHtml,
  buildCorrectiveItem,
  correctiveCode,
  correctiveCodeHtml,
  correctiveCodeMatches,
  correctiveCodeOf,
  correctiveCodeQuery,
  correctiveKey,
  correctiveStatusBadgeHtml,
  findCorrectiveItemByKey,
  isCorrectiveOverdue,
  renderCorrective
};
